import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getReportBranchHeaders } from '../utils/branchStorage';
import { getPaymentMethodLabel } from '../utils/paymentMethod';
import { fetchWithRetry } from '../utils/fetchWithRetry';
import { useSocket } from '../hooks/useSocket';
import { useBranch } from '../contexts/BranchContext';
import { useConfirm } from '../contexts/ConfirmContext';
import Toast from '../components/Toast';
import { buffetBreakdown } from '../utils/buffetBreakdown';

const PAGE_SIZE = 50;

// "วันนี้" ต้องอิงปฏิทินกรุงเทพฯ ไม่ใช่ toISOString() ที่คืนวันที่ UTC — ช่วงตี 0-เจ็ดโมงเช้า
// เวลาไทย (คาบเกี่ยว 17:00-23:59 UTC ของ "เมื่อวาน") ไม่งั้นปุ่มนี้จะเลือกวันที่ผิดไปเลย
// (รูปแบบเดียวกับ bangkokNow() ใน SalesByCategory.jsx)
const bangkokToday = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Bangkok' });

export default function TransactionHistory({ apiUrl }) {
  const navigate = useNavigate();
  const { effectiveBranchId, isAllBranches } = useBranch();
  const [searchParams, setSearchParams] = useSearchParams();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState(searchParams.get('filter') || 'today'); // 'today', 'week', 'month', 'all', 'custom'
  const [selectedDate, setSelectedDate] = useState(bangkokToday());
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    channel: 'all',
    paymentMethod: 'all',
    billType: 'all',
    status: 'all'
  });
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [message, setMessage] = useState(null); // { type: 'success'|'error', text } — แทน alert() เดิม
  const [pagination, setPagination] = useState({ page: 1, pageSize: PAGE_SIZE, total: 0, totalPages: 1 });
  const [stats, setStats] = useState({ totalRevenue: 0, totalTransactions: 0, paid: 0, unpaid: 0, byPaymentMethod: {} });
  const [refreshKey, setRefreshKey] = useState(0);
  const [reprinting, setReprinting] = useState(false);
  const [reprintError, setReprintError] = useState(null);
  const [reprintingRowId, setReprintingRowId] = useState(null); // แยกจาก reprinting ด้านบน (ของปุ่มใน modal รายละเอียด) — ปุ่มในตารางมีแถวของตัวเอง
  const confirm = useConfirm();

  const handleDateRangeChange = (next) => {
    setDateRange(next);
    setSearchParams(next === 'today' ? {} : { filter: next });
  };

  // เปลี่ยน filter/วันที่/คำค้นหา ต้องกลับไปหน้า 1 เสมอ ไม่งั้นค้างอยู่หน้า 3 ของ filter เก่า
  // แล้วเจอ "ไม่มีข้อมูล" ทั้งที่จริงมีแค่มันอยู่หน้าอื่น — เช็คด้วย ref แทนแยกเป็นสอง effect
  // เพราะสอง effect แข่งกัน (ตัวรีเซ็ตหน้ากับตัวโหลดข้อมูล) ทำให้ยิง fetch ซ้ำสองรอบ: รอบแรก
  // ด้วยเลขหน้าเก่าที่ค้างอยู่ (เจอ "ไม่มีข้อมูล" วาบหนึ่ง) แล้วค่อยยิงรอบสองด้วยหน้า 1 ที่ถูกต้อง
  const filterSignatureRef = useRef('');
  const loadTransactionsRequestId = useRef(0);
  useEffect(() => {
    const signature = JSON.stringify({ selectedDate, filters, dateRange, customStartDate, customEndDate, searchQuery });
    const filtersChanged = filterSignatureRef.current !== signature;
    filterSignatureRef.current = signature;

    if (filtersChanged && pagination.page !== 1) {
      setPagination(p => ({ ...p, page: 1 }));
      return; // effect นี้ยิงซ้ำอีกทีเองตอน pagination.page เปลี่ยนเป็น 1 แล้วค่อยโหลดข้อมูล
    }

    loadTransactions();
    loadStats();
  }, [selectedDate, filters, dateRange, customStartDate, customEndDate, searchQuery, pagination.page, refreshKey]);

  // แจ้งเตือนสด — มีบิลใหม่/แก้ไขเข้ามาระหว่างเปิดหน้านี้ค้างไว้ก็โหลดใหม่ทันที
  useSocket(apiUrl, {
    'transaction:created': () => setRefreshKey(k => k + 1),
    'transaction:updated': () => setRefreshKey(k => k + 1),
  }, { token: localStorage.getItem('adminToken'), branchId: isAllBranches ? null : effectiveBranchId });

  const fetchTransactionDetail = async (id) => {
    try {
      setLoadingDetail(true);
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${apiUrl}/api/transaction-history/${id}`, {
        headers: { 'Authorization': `Bearer ${token}`, ...getReportBranchHeaders() }
      });

      if (!response.ok) throw new Error('Failed to load transaction detail');

      const result = await response.json();
      setSelectedTransaction(result.data);
      setShowDetailModal(true);
    } catch (error) {
      console.error('Failed to load transaction detail:', error);
      setMessage({ type: 'error', text: 'ไม่สามารถโหลดรายละเอียดได้' });
    } finally {
      setLoadingDetail(false);
    }
  };

  // Query params ที่ GET / และ GET /summary ต้องกรองเหมือนกันเป๊ะ — export ไม่ใส่ page/pageSize
  // (ปุ่ม Export CSV เติม all=true เองทีหลัง)
  const buildQueryParams = () => {
    const params = new URLSearchParams();

    if (dateRange === 'today') {
      params.append('date', selectedDate);
    } else if (dateRange === 'week') {
      // Get first and last day of current week (Monday-Sunday)
      const now = new Date();
      const dayOfWeek = now.getDay();
      const firstDay = new Date(now);
      firstDay.setDate(now.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1)); // Monday
      const lastDay = new Date(firstDay);
      lastDay.setDate(firstDay.getDate() + 6); // Sunday
      params.append('startDate', firstDay.toISOString().split('T')[0]);
      params.append('endDate', lastDay.toISOString().split('T')[0]);
    } else if (dateRange === 'month') {
      // Get first and last day of current month
      const now = new Date();
      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
      const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
      params.append('startDate', firstDay);
      params.append('endDate', lastDay);
    } else if (dateRange === 'custom') {
      // Custom date range
      if (customStartDate && customEndDate) {
        params.append('startDate', customStartDate);
        params.append('endDate', customEndDate);
      }
    }
    // For 'all', don't add date filters

    if (filters.channel !== 'all') params.append('channel', filters.channel);
    if (filters.paymentMethod !== 'all') params.append('paymentMethod', filters.paymentMethod);
    if (filters.billType !== 'all') params.append('billType', filters.billType);
    if (filters.status !== 'all') params.append('status', filters.status);
    if (searchQuery.trim()) params.append('search', searchQuery.trim());

    return params;
  };

  const loadTransactions = async () => {
    // กันเคส response มาไม่เรียงลำดับ (เช่น กดถัดไปรัวๆ) — ถ้ามี request ใหม่กว่ายิงออกไป
    // ระหว่างที่ request นี้ยังไม่เสร็จ ผลของ request เก่าที่มาทีหลังต้องถูกทิ้ง ไม่งั้นข้อมูล
    // หน้าที่แสดงจะย้อนกลับไปเป็นของหน้าเก่าที่โหลดช้ากว่า
    const requestId = ++loadTransactionsRequestId.current;
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const params = buildQueryParams();
      params.append('page', pagination.page);
      params.append('pageSize', PAGE_SIZE);

      const response = await fetchWithRetry(`${apiUrl}/api/transaction-history?${params}`, {
        headers: { 'Authorization': `Bearer ${token}`, ...getReportBranchHeaders() }
      });

      if (!response.ok) throw new Error('Failed to load transactions');

      const result = await response.json();
      if (requestId !== loadTransactionsRequestId.current) return; // มี request ใหม่กว่าแซงไปแล้ว
      setTransactions(result.data || []);
      if (result.meta?.pagination) setPagination(result.meta.pagination);
    } catch (error) {
      console.error('Failed to load transactions:', error);
      if (requestId !== loadTransactionsRequestId.current) return;
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const params = buildQueryParams();
      const response = await fetchWithRetry(`${apiUrl}/api/transaction-history/summary?${params}`, {
        headers: { 'Authorization': `Bearer ${token}`, ...getReportBranchHeaders() }
      });
      if (!response.ok) throw new Error('Failed to load stats');
      const result = await response.json();
      setStats({
        totalRevenue: result.data.totalRevenue || 0,
        totalTransactions: result.data.totalTransactions || 0,
        paid: result.data.paid || 0,
        unpaid: result.data.unpaid || 0,
        byPaymentMethod: result.data.byPaymentMethod || {}
      });
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const handleReprint = async (transactionId) => {
    try {
      setReprinting(true);
      setReprintError(null);
      const token = localStorage.getItem('adminToken');
      const response = await fetchWithRetry(`${apiUrl}/api/printer/reprint/${transactionId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, ...getReportBranchHeaders() }
      });
      const body = await response.json();
      if (!response.ok) throw new Error(body.error?.message || 'พิมพ์ใบเสร็จไม่สำเร็จ');
    } catch (error) {
      setReprintError(error.message);
    } finally {
      setReprinting(false);
    }
  };

  // ปุ่ม "พิมพ์ซ้ำ" ต่อแถวในตาราง — endpoint เดียวกับปุ่มใน modal รายละเอียด (handleReprint
  // ด้านบน) แค่มี state แยกต่อแถว (reprintingRowId) + ขอ confirm ก่อนเสมอ ต่างจากปุ่มใน modal
  // ที่ไม่มี confirm เพราะอยู่ในหน้าที่เลือกบิลนั้นชัดเจนอยู่แล้ว
  const handleRowReprint = async (tx) => {
    const ok = await confirm(
      `พิมพ์ใบเสร็จโต๊ะ ${tx.tableCode || '-'} (${formatCurrency(tx.amount || 0)} ฿) ซ้ำ?`,
      { title: 'พิมพ์ใบเสร็จซ้ำ', confirmText: 'พิมพ์' }
    );
    if (!ok) return;

    setReprintingRowId(tx.id);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetchWithRetry(`${apiUrl}/api/printer/reprint/${tx.id}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, ...getReportBranchHeaders() }
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.error?.message || 'พิมพ์ใบเสร็จไม่สำเร็จ');
      setMessage({ type: 'success', text: 'ส่งไปที่เครื่องพิมพ์แล้ว' });
    } catch (error) {
      // error ของ Toast ไม่หายเอง (ดู Toast.jsx) ตรงกับที่ต้องการ — ไม่ต้องเขียน logic ค้างเพิ่ม
      setMessage({ type: 'error', text: error.message });
    } finally {
      setReprintingRowId(null);
    }
  };

  // ตัดส่วนหน้า (สาขา-วันที่) ของหมายเลขคำสั่งซื้อออกถ้ายาวเกินจะแสดงพอดีในคอลัมน์ — เหลือ
  // ส่วนท้ายที่เป็นเลขรันนิ่งจริงซึ่งกวาดตาแยกบิลกันง่ายกว่า ส่วนเลขเต็มยังดูได้จาก title ทุกที่
  const truncateOrderId = (id) => {
    if (!id || id.length <= 14) return id;
    return `…${id.slice(-10)}`;
  };

  // ค้นหาทำที่ backend แล้ว (query param search= — ดู buildQueryParams) เพื่อให้หาเจอทั้ง
  // ช่วงวันที่ที่กรองอยู่ ไม่ใช่แค่หน้าที่กำลังเปิดดู — transactions ที่ได้มาจึงกรองแล้วเสมอ
  const filteredTransactions = transactions;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('th-TH').format(amount);
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleString('th-TH', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateTime = (timestamp) => {
    return new Date(timestamp).toLocaleString('th-TH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // การ์ดสรุปตามวิธีชำระเงินมาจาก stats.byPaymentMethod (server aggregate, /summary) —
  // คำนวณจาก "ทุกรายการที่ตรง filter" ไม่ใช่แค่หน้าที่กำลังเปิดดูอยู่ (ต่างจากของเดิมที่
  // reduce จาก filteredTransactions ซึ่งพังทันทีที่มี pagination)
  const paymentBreakdown = stats.byPaymentMethod;

  const [exporting, setExporting] = useState(false);

  const handleExportCSV = async () => {
    try {
      setExporting(true);
      const token = localStorage.getItem('adminToken');
      const params = buildQueryParams();
      params.append('all', 'true');
      const response = await fetchWithRetry(`${apiUrl}/api/transaction-history?${params}`, {
        headers: { 'Authorization': `Bearer ${token}`, ...getReportBranchHeaders() }
      });
      if (!response.ok) throw new Error('Export failed');
      const result = await response.json();
      const allRows = result.data || [];

      const headers = ['เวลาทำรายการ', 'หมายเลขคำสั่งซื้อ', 'โต๊ะ', 'ช่องทางขาย', 'ประเภทบิล', 'วิธีชำระเงิน', 'สถานะ', 'มูลค่า'];
      const rows = allRows.map(tx => [
        formatDateTime(tx.createdAt),
        tx.orderId,
        tx.tableCode,
        tx.channel || 'ทานที่ร้าน',
        tx.billType || 'เต็ม',
        getPaymentMethodLabel(tx.paymentMethod),
        tx.status || '-',
        formatCurrency(tx.amount)
      ]);

      // formatCurrency ใส่ comma คั่นหลักพัน (เช่น "1,292") ถ้าไม่ quote field จะโดนตัดเป็น
      // คนละคอลัมน์ตอนเปิดด้วย Excel/Sheets — quote ทุก field กันไว้เผื่อ field อื่นมี comma/
      // quote/ขึ้นบรรทัดใหม่ปนมาด้วยเหมือนกัน (มาตรฐาน CSV escaping)
      const csvField = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
      const csv = [headers, ...rows].map(row => row.map(csvField).join(',')).join('\n');
      const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `transactions_${selectedDate}.csv`;
      link.click();
    } catch (error) {
      console.error('Failed to export CSV:', error);
      setMessage({ type: 'error', text: '\u0e44\u0e21\u0e48\u0e2a\u0e32\u0e21\u0e32\u0e23\u0e16 export CSV \u0e44\u0e14\u0e49' });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--bg)',
      padding: '2rem'
    }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        <Toast message={message} onDismiss={() => setMessage(null)} />
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '2rem'
        }}>
          <div>
            <button
              onClick={() => navigate('/')}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--muted)',
                fontSize: '1.5rem',
                cursor: 'pointer',
                marginBottom: '0.5rem'
              }}
            >
              ← กลับ
            </button>
            <h1 style={{ color: 'var(--text)', fontSize: '2rem', margin: 0 }}>
              📋 ประวัติการขายของร้าน
            </h1>
          </div>

          {/* Export Button */}
          <button
            onClick={handleExportCSV}
            disabled={exporting || stats.totalTransactions === 0}
            style={{
              padding: '0.75rem 1.5rem',
              background: !exporting && stats.totalTransactions > 0 ? 'var(--ok)' : 'var(--surface-2)',
              color: !exporting && stats.totalTransactions > 0 ? '#1a1206' : 'var(--muted)',
              border: 'none',
              borderRadius: '12px',
              fontSize: '0.95rem',
              fontWeight: '600',
              cursor: !exporting && stats.totalTransactions > 0 ? 'pointer' : 'not-allowed',
              opacity: !exporting && stats.totalTransactions > 0 ? 1 : 0.5
            }}
          >
            {exporting ? '⏳ กำลัง Export...' : '⬇ Export CSV'}
          </button>
        </div>

        {/* Stat Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '1rem',
          marginBottom: '1.5rem'
        }}>
          <div style={{ background: 'var(--surface)', borderRadius: '16px', padding: '1.25rem', border: '1px solid var(--border)' }}>
            <div style={{ color: 'var(--muted)', fontSize: '0.85rem', marginBottom: '0.4rem' }}>💵 รายได้ทั้งหมด</div>
            <div style={{ color: 'var(--text)', fontSize: '1.5rem', fontWeight: '700' }}>{formatCurrency(stats.totalRevenue)} ฿</div>
          </div>
          <div style={{ background: 'var(--surface)', borderRadius: '16px', padding: '1.25rem', border: '1px solid var(--border)' }}>
            <div style={{ color: 'var(--muted)', fontSize: '0.85rem', marginBottom: '0.4rem' }}>✅ ชำระแล้ว</div>
            <div style={{ color: 'var(--ok)', fontSize: '1.5rem', fontWeight: '700' }}>{formatCurrency(stats.paid)} ฿</div>
          </div>
          <div style={{ background: 'var(--surface)', borderRadius: '16px', padding: '1.25rem', border: '1px solid var(--border)' }}>
            <div style={{ color: 'var(--muted)', fontSize: '0.85rem', marginBottom: '0.4rem' }}>⏳ ยังไม่ชำระ</div>
            <div style={{ color: 'var(--warn)', fontSize: '1.5rem', fontWeight: '700' }}>{formatCurrency(stats.unpaid)} ฿</div>
          </div>
          <div style={{ background: 'var(--surface)', borderRadius: '16px', padding: '1.25rem', border: '1px solid var(--border)' }}>
            <div style={{ color: 'var(--muted)', fontSize: '0.85rem', marginBottom: '0.4rem' }}>🧾 จำนวนธุรกรรม</div>
            <div style={{ color: 'var(--text)', fontSize: '1.5rem', fontWeight: '700' }}>{stats.totalTransactions}</div>
          </div>
        </div>

        {/* Payment Method Report — ย้ายมาไว้บนสุด (เดิมอยู่ท้ายหน้า ใต้ตาราง/pagination) */}
        {!loading && filteredTransactions.length > 0 && (
          <div style={{
            marginBottom: '1.5rem',
            padding: '1.5rem',
            background: 'var(--surface)',
            borderRadius: '16px',
            border: '1px solid var(--border)'
          }}>
            <div style={{ color: 'var(--text-2)', fontWeight: '600', marginBottom: '1rem' }}>
              📊 สรุปตามวิธีชำระเงิน
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
              gap: '0.75rem'
            }}>
              {Object.entries(paymentBreakdown).map(([method, { count, total }]) => (
                <div
                  key={method}
                  style={{
                    padding: '1rem',
                    background: 'var(--surface-2)',
                    borderRadius: '12px',
                    border: '1px solid var(--border)'
                  }}
                >
                  <div style={{ color: 'var(--muted)', fontSize: '0.85rem', marginBottom: '0.35rem' }}>
                    {getPaymentMethodLabel(method)}
                  </div>
                  <div style={{ color: 'var(--text)', fontSize: '1.1rem', fontWeight: '700' }}>
                    {formatCurrency(total)} บาท
                  </div>
                  <div style={{ color: 'var(--muted)', fontSize: '0.8rem' }}>
                    {count} รายการ
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Date Range Buttons & Filters */}
        <div style={{
          background: 'var(--surface)',
          borderRadius: '16px',
          padding: '1.5rem',
          marginBottom: '1.5rem',
          border: '1px solid var(--border)'
        }}>
          {/* Date Range Buttons */}
          <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
            {[
              { key: 'today', label: '📅 วันนี้', onClick: () => { handleDateRangeChange('today'); setSelectedDate(bangkokToday()); } },
              { key: 'week', label: '📊 สัปดาห์นี้', onClick: () => handleDateRangeChange('week') },
              { key: 'month', label: '📊 เดือนนี้', onClick: () => handleDateRangeChange('month') },
              { key: 'all', label: '📚 ทั้งหมด', onClick: () => handleDateRangeChange('all') },
              { key: 'custom', label: '📆 กำหนดเอง', onClick: () => handleDateRangeChange('custom') }
            ].map(({ key, label, onClick }) => (
              <button
                key={key}
                onClick={onClick}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: dateRange === key ? 'var(--accent)' : 'var(--surface-2)',
                  color: dateRange === key ? '#1a1206' : 'var(--text-2)',
                  border: dateRange === key ? 'none' : '1px solid var(--border-strong)',
                  borderRadius: '12px',
                  fontSize: '0.95rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Custom Date Range Picker */}
          {dateRange === 'custom' && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '1rem',
              marginBottom: '1.5rem',
              padding: '1rem',
              background: 'var(--surface-2)',
              borderRadius: '12px',
              border: '1px solid var(--border)'
            }}>
              <div>
                <label style={{ fontSize: '0.85rem', color: 'var(--muted)', display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                  📅 วันที่เริ่มต้น
                </label>
                <input
                  type="date"
                  className="dark-input"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', fontSize: '0.95rem' }}
                />
              </div>
              <div>
                <label style={{ fontSize: '0.85rem', color: 'var(--muted)', display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                  📅 วันที่สิ้นสุด
                </label>
                <input
                  type="date"
                  className="dark-input"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', fontSize: '0.95rem' }}
                />
              </div>
            </div>
          )}

          {/* Search */}
          <div style={{ marginBottom: '1rem' }}>
            <input
              type="text"
              className="dark-input"
              placeholder="🔍 ค้นหา (เลขที่ใบเสร็จ, โต๊ะ, จำนวนเงิน)"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ width: '100%', padding: '0.75rem 1rem', borderRadius: '8px', fontSize: '0.95rem' }}
            />
          </div>

          {/* Filter Options */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 160px), 1fr))',
            gap: '1rem'
          }}>
            <div>
              <label style={{ fontSize: '0.9rem', color: 'var(--text-2)', display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                ช่องทางขาย
              </label>
              <select
                className="dark-input"
                value={filters.channel}
                onChange={(e) => setFilters({...filters, channel: e.target.value})}
                style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', fontSize: '0.95rem' }}
              >
                <option value="all">ทั้งหมด</option>
                <option value="dine-in">ทานที่ร้าน</option>
                <option value="takeaway">กลับบ้าน</option>
                <option value="delivery">เดลิเวอรี</option>
              </select>
            </div>

            <div>
              <label style={{ fontSize: '0.9rem', color: 'var(--text-2)', display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                วิธีชำระเงิน
              </label>
              <select
                className="dark-input"
                value={filters.paymentMethod}
                onChange={(e) => setFilters({...filters, paymentMethod: e.target.value})}
                style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', fontSize: '0.95rem' }}
              >
                <option value="all">ทั้งหมด</option>
                <option value="cash">เงินสด</option>
                <option value="transfer">โอนเข้า</option>
                <option value="card">บัตร</option>
                <option value="qr">QR Code</option>
                <option value="government">โครงการรัฐ</option>
              </select>
            </div>

            <div>
              <label style={{ fontSize: '0.9rem', color: 'var(--text-2)', display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                ประเภทบิล
              </label>
              <select
                className="dark-input"
                value={filters.billType}
                onChange={(e) => setFilters({...filters, billType: e.target.value})}
                style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', fontSize: '0.95rem' }}
              >
                <option value="all">ทั้งหมด</option>
                <option value="full">เต็ม</option>
                <option value="abbreviated">ย่อ</option>
              </select>
            </div>

            <div>
              <label style={{ fontSize: '0.9rem', color: 'var(--text-2)', display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                สถานะ
              </label>
              <select
                className="dark-input"
                value={filters.status}
                onChange={(e) => setFilters({...filters, status: e.target.value})}
                style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', fontSize: '0.95rem' }}
              >
                <option value="all">ทั้งหมด</option>
                <option value="completed">สำเร็จ</option>
                <option value="pending">รอดำเนินการ</option>
                <option value="cancelled">ยกเลิก</option>
              </select>
            </div>
          </div>
        </div>

        {/* Table */}
        <div style={{
          background: 'var(--surface)',
          borderRadius: '16px',
          border: '1px solid var(--border)',
          overflow: 'hidden'
        }}>
          {loading ? (
            <div style={{ padding: '4rem', textAlign: 'center', color: 'var(--muted)' }}>
              กำลังโหลด...
            </div>
          ) : filteredTransactions.length === 0 ? (
            <div style={{ padding: '4rem', textAlign: 'center', color: 'var(--muted)' }}>
              ไม่พบข้อมูลรายการขาย
            </div>
          ) : (
            <div className="responsive-table-wrapper">
            <table className="table-nowrap table-dark" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                {/* index.css ทาพื้นไล่สีอ่อนให้ `table th` โดยตรง ซึ่งทับพื้นมืดที่ตั้งไว้ที่ <tr> ตารางธีมมืดจึงต้องทับพื้นที่ th เอง ไม่งั้นหัวตารางสีอ่อนจะจมหายไปบนพื้นสว่าง */}
                <tr style={{ backgroundColor: 'var(--surface-2)' }}>
                  {['เวลาทำรายการ', 'หมายเลขคำสั่งซื้อ', 'โต๊ะ', 'ช่องทางขาย', 'ประเภทบิล', 'วิธีชำระเงิน', 'สถานะ'].map(h => (
                    <th key={h} style={{ padding: '1rem', textAlign: 'left', background: 'var(--surface-2)', color: 'var(--accent)', fontSize: '1rem', fontWeight: '700' }}>{h}</th>
                  ))}
                  <th style={{ padding: '1rem', textAlign: 'right', background: 'var(--surface-2)', color: 'var(--accent)', fontSize: '1rem', fontWeight: '700' }}>มูลค่า</th>
                  <th style={{ padding: '1rem', textAlign: 'center', background: 'var(--surface-2)', color: 'var(--accent)', fontSize: '1rem', fontWeight: '700' }}>ดู</th>
                </tr>
              </thead>
              <tbody>
                {filteredTransactions.map((tx, index) => (
                  <tr
                    key={tx.id || index}
                    onClick={() => fetchTransactionDetail(tx.id)}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') fetchTransactionDetail(tx.id);
                    }}
                    style={{
                      borderBottom: '1px solid var(--border)',
                      cursor: 'pointer'
                    }}
                  >
                    <td style={{ padding: '0.75rem 1rem', color: 'var(--text-2)', fontSize: '0.95rem', fontWeight: '500' }}>
                      {tx.createdAt ? formatTime(tx.createdAt) : '-'}
                    </td>
                    <td
                      style={{ padding: '0.75rem 1rem', color: 'var(--info)', fontSize: '0.95rem', fontWeight: '600', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}
                      title={tx.orderId || tx.tableCode || ''}
                    >
                      {truncateOrderId(tx.orderId) || tx.tableCode || '-'}
                    </td>
                    {/* ชื่อโต๊ะ (tableName) ใส่เป็น title ไว้ให้เอาเมาส์ชี้ดูได้ เพราะบางสาขา
                        ตั้งชื่อยาวกว่ารหัส เช่น "A1" กับ "โซนกลางแจ้ง 1" — โชว์รหัสในตาราง
                        ให้ตรงกับ CSV ที่ export อยู่แล้ว และกวาดตาหาโต๊ะได้เร็วกว่า */}
                    <td
                      style={{ padding: '0.75rem 1rem', color: 'var(--text-2)', fontSize: '0.95rem', fontWeight: '600' }}
                      title={tx.tableName || ''}
                    >
                      {tx.tableCode || '-'}
                    </td>
                    <td style={{ padding: '0.75rem 1rem', color: 'var(--text-2)', fontSize: '0.95rem', fontWeight: '500' }}>
                      {tx.channel === 'dine-in' ? 'ทานที่ร้าน' :
                       tx.channel === 'takeaway' ? 'กลับบ้าน' :
                       tx.channel === 'delivery' ? 'เดลิเวอรี' : 'ทานที่ร้าน'}
                    </td>
                    <td style={{ padding: '0.75rem 1rem', color: 'var(--text-2)', fontSize: '0.95rem', fontWeight: '500' }}>
                      {tx.billType === 'abbreviated' ? 'ย่อ' : 'เต็ม'}
                    </td>
                    <td style={{ padding: '0.75rem 1rem', color: 'var(--text-2)', fontSize: '0.95rem', fontWeight: '500' }}>
                      {getPaymentMethodLabel(tx.paymentMethod)}
                    </td>
                    <td style={{ padding: '0.75rem 1rem' }}>
                      <span style={{
                        padding: '0.4rem 0.85rem',
                        borderRadius: '12px',
                        fontSize: '0.9rem',
                        fontWeight: '700',
                        background: tx.status === 'completed' ? 'var(--ok)' :
                                   tx.status === 'pending' ? 'var(--warn)' :
                                   'var(--danger)',
                        color: tx.status === 'cancelled' ? '#fff' : '#1a1206'
                      }}>
                        {tx.status === 'completed' ? '✓ สำเร็จ' :
                         tx.status === 'pending' ? '⏳ รอ' :
                         '✕ ยกเลิก'}
                      </span>
                    </td>
                    <td style={{ padding: '0.75rem 1rem', color: 'var(--ok)', fontSize: '1rem', fontWeight: '700', textAlign: 'right' }}>
                      {formatCurrency(tx.amount || 0)} ฿
                    </td>
                    <td style={{ padding: '0.75rem 1rem', textAlign: 'center' }}>
                      <button
                        disabled={reprintingRowId === tx.id}
                        onClick={(e) => { e.stopPropagation(); handleRowReprint(tx); }}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '0.4rem',
                          minHeight: '36px',
                          background: 'transparent',
                          border: '1px solid var(--border-strong, #2c3140)',
                          color: reprintingRowId === tx.id ? 'var(--muted)' : 'var(--text-2)',
                          fontSize: '0.85rem',
                          fontWeight: '600',
                          padding: '0.4rem 0.85rem',
                          borderRadius: '8px',
                          cursor: reprintingRowId === tx.id ? 'not-allowed' : 'pointer'
                        }}
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d="M6 9V3h12v6" />
                          <rect x="2" y="9" width="20" height="9" rx="2" />
                          <path d="M6 14h12v8H6z" />
                        </svg>
                        {reprintingRowId === tx.id ? 'กำลังส่ง...' : 'พิมพ์ซ้ำ'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {!loading && filteredTransactions.length > 0 && (
          <div style={{
            marginTop: '1.5rem',
            padding: '1.25rem 1.5rem',
            background: 'var(--surface)',
            borderRadius: '16px',
            border: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '1rem'
          }}>
            <div style={{ color: 'var(--muted)' }}>
              ทั้งหมด {stats.totalTransactions} รายการ — รวม {formatCurrency(stats.totalRevenue)} บาท
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <button
                onClick={() => setPagination(p => ({ ...p, page: Math.max(1, p.page - 1) }))}
                disabled={pagination.page <= 1}
                style={{
                  padding: '0.5rem 1rem',
                  background: pagination.page <= 1 ? 'var(--surface-2)' : 'var(--border-strong)',
                  color: 'var(--text)',
                  border: '1px solid var(--border)',
                  borderRadius: '8px',
                  cursor: pagination.page <= 1 ? 'not-allowed' : 'pointer',
                  opacity: pagination.page <= 1 ? 0.5 : 1
                }}
              >
                ← ก่อนหน้า
              </button>
              <span style={{ color: 'var(--text-2)', fontSize: '0.9rem' }}>
                หน้า {pagination.page} / {pagination.totalPages}
              </span>
              <button
                onClick={() => setPagination(p => ({ ...p, page: Math.min(p.totalPages, p.page + 1) }))}
                disabled={pagination.page >= pagination.totalPages}
                style={{
                  padding: '0.5rem 1rem',
                  background: pagination.page >= pagination.totalPages ? 'var(--surface-2)' : 'var(--border-strong)',
                  color: 'var(--text)',
                  border: '1px solid var(--border)',
                  borderRadius: '8px',
                  cursor: pagination.page >= pagination.totalPages ? 'not-allowed' : 'pointer',
                  opacity: pagination.page >= pagination.totalPages ? 0.5 : 1
                }}
              >
                ถัดไป →
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Transaction Detail Modal */}
      {showDetailModal && selectedTransaction && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '1rem'
          }}
          onClick={() => setShowDetailModal(false)}
        >
          <div
            style={{
              background: 'var(--surface)',
              borderRadius: '16px',
              maxWidth: '500px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              border: '1px solid var(--border)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{
              padding: '1.5rem',
              borderBottom: '1px solid var(--border)',
              background: 'var(--surface-2)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ color: 'var(--muted)', fontSize: '0.85rem', marginBottom: '0.25rem' }}>
                    ใบเสร็จ
                  </div>
                  <div style={{ color: 'var(--accent)', fontSize: '1.1rem', fontWeight: '700', fontFamily: 'monospace' }}>
                    {selectedTransaction.receiptId || '-'}
                  </div>
                </div>
                <button
                  onClick={() => setShowDetailModal(false)}
                  style={{
                    background: 'var(--border-strong)',
                    border: 'none',
                    color: 'var(--text)',
                    width: '32px',
                    height: '32px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '1.2rem'
                  }}
                >
                  ✕
                </button>
              </div>
              <div style={{
                display: 'flex',
                gap: '1.5rem',
                marginTop: '1rem',
                color: 'var(--text-2)',
                fontSize: '0.9rem'
              }}>
                <div>🪑 โต๊ะ {selectedTransaction.tableName || selectedTransaction.tableCode}</div>
                <div>👥 {typeof selectedTransaction.guests === 'object'
                  ? `${(selectedTransaction.guests?.adults || 0) + (selectedTransaction.guests?.children || 0)}`
                  : (selectedTransaction.guests || '-')} คน</div>
              </div>
              <div style={{ color: 'var(--muted)', fontSize: '0.85rem', marginTop: '0.5rem' }}>
                📅 {selectedTransaction.createdAt ? formatDateTime(selectedTransaction.createdAt) : '-'}
              </div>
            </div>

            {/* Order Items */}
            {selectedTransaction.orders && selectedTransaction.orders.length > 0 && (
              <div style={{ padding: '1.5rem', borderBottom: '1px solid var(--border)' }}>
                <div style={{ color: 'var(--muted)', fontSize: '0.85rem', marginBottom: '1rem', fontWeight: '600' }}>
                  รายการสั่ง
                </div>
                {selectedTransaction.orders.flatMap((order, orderIdx) => {
                  // Handle both flat items array and nested order.items structure
                  const items = order.items || [order];
                  return items.map((item, itemIdx) => (
                    <div key={`${orderIdx}-${itemIdx}`} style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '0.5rem 0',
                      borderBottom: '1px solid var(--border)'
                    }}>
                      <div style={{ color: 'var(--text)', fontSize: '0.9rem' }}>
                        {item.name || item.menuName || 'รายการ'}
                        {item.options && item.options.length > 0 && (
                          <span style={{ color: 'var(--muted)', fontSize: '0.8rem', marginLeft: '0.5rem' }}>
                            ({item.options.map(opt => typeof opt === 'object' ? opt.name : opt).join(', ')})
                          </span>
                        )}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <span style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>
                          x{item.quantity || 1}
                        </span>
                        <span style={{ color: 'var(--text)', fontWeight: '600', minWidth: '80px', textAlign: 'right' }}>
                          {formatCurrency((item.price || 0) * (item.quantity || 1))} ฿
                        </span>
                      </div>
                    </div>
                  ));
                })}
              </div>
            )}

            {/* Cost Breakdown */}
            <div style={{ padding: '1.5rem', borderBottom: '1px solid var(--border)' }}>
              {(() => {
                const rows = buffetBreakdown(selectedTransaction.guests, selectedTransaction.pricing, selectedTransaction.buffetTotal);
                const rowStyle = { display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', color: 'var(--muted)', fontSize: '0.9rem' };
                if (rows.length === 0) {
                  return (
                    <div style={rowStyle}>
                      <span>บุฟเฟ่ต์</span>
                      <span style={{ color: 'var(--text)' }}>{formatCurrency(selectedTransaction.buffetTotal || 0)} ฿</span>
                    </div>
                  );
                }
                return rows.map(r => (
                  <div key={r.label} style={rowStyle}>
                    <span>
                      {r.label}
                      {r.qty > 0 && ` ${r.qty} คน × ${formatCurrency(r.unit)}`}
                    </span>
                    <span style={{ color: 'var(--text)' }}>{formatCurrency(r.amount)} ฿</span>
                  </div>
                ));
              })()}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '0.5rem 0',
                color: 'var(--muted)',
                fontSize: '0.9rem'
              }}>
                <span>เมนูเพิ่ม</span>
                <span style={{ color: 'var(--text)' }}>{formatCurrency(selectedTransaction.menuTotal || 0)} ฿</span>
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '1rem 0 0.5rem',
                marginTop: '0.5rem',
                borderTop: '2px solid var(--border)',
                fontSize: '1.1rem',
                fontWeight: '700'
              }}>
                <span style={{ color: 'var(--text)' }}>รวมทั้งหมด</span>
                <span style={{ color: 'var(--ok)' }}>{formatCurrency(selectedTransaction.grandTotal || 0)} ฿</span>
              </div>
            </div>

            {/* Payment Info */}
            <div style={{ padding: '1.5rem' }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: '0.75rem'
              }}>
                <span style={{ color: 'var(--muted)', fontSize: '0.9rem' }}>วิธีชำระเงิน</span>
                <span style={{ color: 'var(--text)', fontWeight: '500' }}>
                  {getPaymentMethodLabel(selectedTransaction.paymentMethod)}
                </span>
              </div>
              {selectedTransaction.payments && selectedTransaction.payments.length > 1 && (
                <div style={{ marginBottom: '0.75rem', paddingLeft: '1rem' }}>
                  {selectedTransaction.payments.map((p, idx) => (
                    <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: 'var(--muted)', marginBottom: '0.25rem' }}>
                      <span>{getPaymentMethodLabel(p.method)}</span>
                      <span>{formatCurrency(p.amount)} ฿</span>
                    </div>
                  ))}
                </div>
              )}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: '0.75rem'
              }}>
                <span style={{ color: 'var(--muted)', fontSize: '0.9rem' }}>สถานะ</span>
                <span style={{
                  padding: '0.25rem 0.75rem',
                  borderRadius: '12px',
                  fontSize: '0.85rem',
                  fontWeight: '600',
                  background: selectedTransaction.status === 'completed' ? 'rgba(63, 191, 127, 0.2)' : 'rgba(245, 181, 68, 0.2)',
                  color: selectedTransaction.status === 'completed' ? 'var(--ok)' : 'var(--warn)'
                }}>
                  {selectedTransaction.status === 'completed' ? '✅ สำเร็จ' : '⏳ รอดำเนินการ'}
                </span>
              </div>
              {selectedTransaction.notes && (
                <div style={{ marginTop: '1rem' }}>
                  <span style={{ color: 'var(--muted)', fontSize: '0.9rem' }}>หมายเหตุ: </span>
                  <span style={{ color: 'var(--text)' }}>{selectedTransaction.notes}</span>
                </div>
              )}
            </div>

            {/* Reprint */}
            <div style={{ padding: '0 1.5rem 1rem' }}>
              <button
                onClick={() => handleReprint(selectedTransaction.id)}
                disabled={reprinting}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: reprinting ? 'var(--surface-2)' : 'rgba(242, 140, 56, 0.15)',
                  color: 'var(--text)',
                  border: '1px solid rgba(242, 140, 56, 0.4)',
                  borderRadius: '12px',
                  fontSize: '0.95rem',
                  fontWeight: '600',
                  cursor: reprinting ? 'not-allowed' : 'pointer',
                  opacity: reprinting ? 0.7 : 1
                }}
              >
                {reprinting ? '⏳ กำลังพิมพ์...' : '🖨️ พิมพ์ใบเสร็จซ้ำ'}
              </button>
              {reprintError && (
                <div style={{ marginTop: '0.5rem', color: 'var(--danger)', fontSize: '0.85rem', textAlign: 'center' }}>
                  {reprintError}
                </div>
              )}
            </div>

            {/* Close Button */}
            <div style={{ padding: '0 1.5rem 1.5rem' }}>
              <button
                onClick={() => { setShowDetailModal(false); setReprintError(null); }}
                style={{
                  width: '100%',
                  padding: '0.875rem',
                  background: 'var(--surface-2)',
                  color: 'var(--text)',
                  border: '1px solid var(--border-strong)',
                  borderRadius: '12px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                ปิด
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading Overlay */}
      {loadingDetail && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001
        }}>
          <div style={{ color: 'var(--text)', fontSize: '1.2rem' }}>กำลังโหลด...</div>
        </div>
      )}
    </div>
  );
}
